import React from "react";
import { graphql } from "gatsby";
import { SanitySlice, SEO } from "@ottomotors/ottomotors.com/components";
import { FaqSchema, ProductSchema, extractFaqItems } from "@ottomotors/ottomotors.com/components/SEO/JsonLd";
import { AnchorLinks } from "@ottomotors/ottomotors.com/slices";
import { useSiteMetadata } from "@ottomotors/ottomotors.com/hooks";
import { IOttoMotorsProduct } from "@ottomotors/ottomotors-sanity";

import * as styles from "./styles.module.scss";

interface IProps {
  data: {
    sanityOttomotorsProduct: IOttoMotorsProduct;
  };
}

const Product = ({ data: staticData }: IProps) => {
  const { sanityOttomotorsProduct } = staticData;
  const { pagebuilder, hasAnchorLinks, sideAnchorLinks } = sanityOttomotorsProduct || {};

  if (!pagebuilder?.slices?.[0]) {
    return null;
  }

  return (
    <div className={styles.container}>
      {hasAnchorLinks && <AnchorLinks data={{ slices: pagebuilder.slices, side: sideAnchorLinks }} />}

      {pagebuilder.slices.map((slice: any) => (
        <div key={slice._key}>
          <SanitySlice key={slice._key} data={slice} />
        </div>
      ))}
    </div>
  );
};

export default Product;

export const query = graphql`
  query Product($id: String!) {
    sanityOttomotorsProduct(id: { eq: $id }) {
      title
      slug {
        current
      }
      hasAnchorLinks
      sideAnchorLinks

      pagebuilder {
        ...OttomotorsPagebuilderFragment
      }

      productSchemaData {
        price
        priceCurrency
        ratingValue
        ratingCount
      }

      seo {
        ...SeoFragment
      }
    }
  }
`;

export const Head = ({ data: { sanityOttomotorsProduct } }: IProps) => {
  const {
    slug: { current: slug },
    title,
    seo,
    pagebuilder,
    productSchemaData,
  } = sanityOttomotorsProduct;

  const meta = useSiteMetadata();
  const pathname = slug === `/` ? `/` : `/${slug}/`;
  const seoTitle = seo?.title || title;
  const faqItems = extractFaqItems(pagebuilder?.slices);
  const productUrl = `${meta.siteUrl}${pathname}`.replace(/([^:])\/\//g, "$1/");
  const hasProductSchema = productSchemaData?.price || productSchemaData?.ratingValue;

  return (
    <>
      <SEO
        pageTitle={seoTitle}
        pathname={pathname}
        description={seo?.description}
        keywords={seo?.keywords}
        openGraphImageUrl={seo?.image?.asset?.url}
        noIndex={seo?.noIndex}
      />
      {hasProductSchema && (
        <ProductSchema
          name={seoTitle}
          description={seo?.description}
          imageUrl={seo?.image?.asset?.url}
          url={productUrl}
          price={productSchemaData?.price}
          priceCurrency={productSchemaData?.priceCurrency}
          ratingValue={productSchemaData?.ratingValue}
          ratingCount={productSchemaData?.ratingCount}
        />
      )}
      {faqItems.length > 0 && <FaqSchema faqItems={faqItems} />}
    </>
  );
};
